import React from 'react'
import {
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react'
import { AddIcon, ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@chakra-ui/icons'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import xhr from 'xhr'
import useAuth from 'hooks/useAuth'
import DatePicker from 'components/datePicker/DatePicker'
import AiPagination from 'components/monitoring/ai/AiPagination'
import useProgramTypes from './prospectos/useProgramTypes'
import TransferProspectModal from './prospectos/TransferProspectModal'
import { contactColor, daysSince, loadLastContacts, relativeDays } from './prospectos/lastContact'
import { BRAND, EmptyState, PageHeader, SectionCard, StatusBadge } from './components/ui'
import {
  DESK_VIEWS,
  PROSPECT_STATUS,
  channelLabel,
  programLabel,
  statusInfo,
} from './prospectos/constants'

const REFERRAL_PROGRAMS = ['convenio-referido', 'convenio-referido-fb']
const PAGE_SIZE = 20

const withoutReferralAgreements = (list) =>
  list.filter((el) => !REFERRAL_PROGRAMS.includes(el.program))

function HomeAsesoras() {
  const { user } = useAuth()
  const toast = useToast()
  // Cualquier asesora activa puede transferir (con motivo).
  const canTransfer = Boolean(user.adviser?.active)
  const programTypes = useProgramTypes()

  const [prospects, setProspects] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [prospectReminders, setProspectReminders] = React.useState([])
  const [fetching, isFetching] = React.useState(true)
  const [appliedFilters, setAppliedFilters] = React.useState({})
  const [lastContacts, setLastContacts] = React.useState({})

  const [view, setView] = React.useState('seguimiento')
  const [search, setSearch] = React.useState('')
  const [showFilter, setShowFilter] = React.useState(false)
  const [filterState, setFilterState] = React.useState('')
  const [dateFilter, setDateFilter] = React.useState(null)
  const [dateFilterEnd, setDateFilterEnd] = React.useState(null)
  const [cellphone, setCellphone] = React.useState('')
  const [email, setEmail] = React.useState('')

  const [showTransfer, setShowTransfer] = React.useState(false)
  const [cellphoneTransfer, setCellphoneTransfer] = React.useState('')
  // true cuando la tabla muestra el resultado de "Transferir prospectos" (de cualquier asesora).
  const [transferMode, setTransferMode] = React.useState(false)
  const [transferProspect, setTransferProspect] = React.useState(null)

  // Arma la consulta del Escritorio: la vista define los estados, salvo que
  // la asesora haya elegido un estado puntual en "Más filtros".
  // Strapi limita cada consulta a 250 registros (config/api.js → maxLimit), por
  // eso la lista se pagina y la búsqueda se hace en el servidor.
  const buildProspectsUrl = ({ viewValue, filters = {}, pageNumber = 1, term = '' }) => {
    const parts = [
      `/prospects?filters[adviser][id][$eq]=${user.adviser.id}`,
      'filters[program][$ne]=convenio-referido',
      `pagination[page]=${pageNumber}`,
      `pagination[pageSize]=${PAGE_SIZE}`,
      'sort[0]=createdAt:desc',
      'populate=country',
    ]

    const cleanTerm = term.trim()
    if (cleanTerm) {
      const encoded = encodeURIComponent(cleanTerm)
      parts.push(`filters[$or][0][name][$containsi]=${encoded}`)
      parts.push(`filters[$or][1][cellphone][$containsi]=${encoded}`)
      parts.push(`filters[$or][2][email][$containsi]=${encoded}`)
      // Solo se compara con el ID si cabe en un entero de Postgres; un celular no.
      if (/^\d{1,9}$/.test(cleanTerm)) parts.push(`filters[$or][3][id][$eq]=${encoded}`)
    }

    const statuses = filters.status
      ? [filters.status]
      : DESK_VIEWS.find((el) => el.value === viewValue)?.statuses

    if (statuses) {
      statuses.forEach((status, index) => {
        parts.push(`filters[status][$in][${index}]=${status}`)
      })
    }

    if (!filters.status && viewValue === 'seguimiento' && !filters.dateStart) {
      parts.push('filters[createdAt][$gte]=2023-06-01')
    }

    if (filters.email) parts.push(`filters[email][$eq]=${encodeURIComponent(filters.email)}`)
    if (filters.cellphone) parts.push(`filters[cellphone][$eq]=${encodeURIComponent(filters.cellphone)}`)
    if (filters.dateStart) parts.push(`filters[createdAt][$gte]=${filters.dateStart}`)
    if (filters.dateEnd) parts.push(`filters[createdAt][$lte]=${filters.dateEnd}`)

    return parts.join('&')
  }

  const loadProspects = async (viewValue, filters = {}, pageNumber = 1, term = search) => {
    try {
      isFetching(true)
      const res = await xhr(buildProspectsUrl({ viewValue, filters, pageNumber, term }))
      const list = withoutReferralAgreements(res.data.data)
      setProspects(list)
      setTransferMode(false)
      loadLastContacts(list)
        .then(setLastContacts)
        .catch((error) => {
          console.error(error)
          setLastContacts({})
        })
      setTotal(res.data.meta?.pagination?.total ?? res.data.data.length)
      setPage(pageNumber)
      setAppliedFilters(filters)
    } catch (error) {
      console.error(error)
      setProspects([])
      setTotal(0)
      toast({ title: 'No se pudieron cargar los prospectos.', status: 'error', duration: 4000, isClosable: true })
    } finally {
      isFetching(false)
    }
  }

  const loadReminders = async () => {
    try {
      const fechaHoy = dayjs().format('YYYY-MM-DD')
      const res = await xhr(
        `/prospect-reminders?filters[prospect][adviser]=${user.adviser.id}&filters[active][$eq]=true&filters[date]=${fechaHoy}&populate=prospect&sort[0]=time:asc`
      )
      setProspectReminders(res.data.data)
    } catch (error) {
      console.error(error)
    }
  }

  React.useEffect(() => {
    loadReminders()
  }, [])

  // Búsqueda con espera corta para no consultar en cada tecla.
  React.useEffect(() => {
    const timer = setTimeout(() => loadProspects(view, appliedFilters, 1, search), search ? 400 : 0)
    return () => clearTimeout(timer)
  }, [search])

  const handleViewChange = (value) => {
    setView(value)
    loadProspects(value, currentFilters(), 1)
  }

  const currentFilters = () => {
    const filters = { status: filterState, email: email.trim(), cellphone: cellphone.trim() }
    if (dateFilter && dateFilterEnd) {
      // createdAt se guarda en UTC; Colombia es UTC-5.
      filters.dateStart = `${dayjs(dateFilter).format('YYYY-MM-DD')}T05:00:00`
      filters.dateEnd = dayjs(`${dayjs(dateFilterEnd).format('YYYY-MM-DD')}T23:59:59`)
        .add(5, 'hour')
        .format('YYYY-MM-DDTHH:mm:ss')
    }
    return filters
  }

  const handleFilter = () => {
    if (dateFilter && !dateFilterEnd) {
      toast({ title: 'Selecciona la fecha fin del rango.', status: 'warning', duration: 3000, isClosable: true })
      return
    }
    loadProspects(view, currentFilters(), 1)
  }

  const handleClearFilters = () => {
    setFilterState('')
    setDateFilter(null)
    setDateFilterEnd(null)
    setCellphone('')
    setEmail('')
    loadProspects(view, {}, 1)
  }

  const handleSearchTransfer = async () => {
    if (!cellphoneTransfer.trim()) return
    try {
      isFetching(true)
      const res = await xhr(
        `/prospects?sort[0]=createdAt:desc&populate=country&populate=adviser.user&filters[cellphone][$containsi]=${encodeURIComponent(cellphoneTransfer.replace(/\D/g, ''))}`
      )
      const found = withoutReferralAgreements(res.data.data)
      setProspects(found)
      setTotal(found.length)
      setPage(1)
      setTransferMode(true)
      loadLastContacts(found).then(setLastContacts).catch(() => setLastContacts({}))
    } catch (error) {
      console.error(error)
    } finally {
      isFetching(false)
    }
  }

  const handleTransferred = () => {
    if (transferMode) handleSearchTransfer()
    else loadProspects(view, appliedFilters, page)
  }


  const visibleProspects = prospects
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const adviserName = user.first_name || 'ti'

  return (
    <Box>
      <PageHeader
        title="Escritorio"
        subtitle={`Prospectos asignados a ${adviserName}`}
        actions={
          <Button
            as={Link}
            to="/plataforma/asesoras/prospectos/nuevo"
            leftIcon={<AddIcon boxSize={3} />}
            bg={BRAND}
            color="white"
            _hover={{ bg: '#463fd1' }}
          >
            Agregar prospecto
          </Button>
        }
      />

      <Tabs colorScheme="purple">
        <TabList>
          <Tab fontWeight="500">Prospectos</Tab>
          <Tab fontWeight="500">Recordatorios de hoy ({prospectReminders.length})</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0} pt={5}>
            {canTransfer && (
              <SectionCard mb={4} padding={4}>
                <Flex
                  as="button"
                  type="button"
                  w="100%"
                  justify="space-between"
                  align="center"
                  onClick={() => setShowTransfer(!showTransfer)}
                  fontWeight="600"
                  fontSize="sm"
                  color="gray.700"
                >
                  Transferir prospectos
                  {showTransfer ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Flex>
                <Collapse in={showTransfer} animateOpacity>
                  <Flex gap={3} mt={3} align="flex-end" wrap="wrap">
                    <FormControl maxW="320px">
                      <FormLabel fontSize="sm">Celular del prospecto</FormLabel>
                      <Input
                        bg="white"
                        placeholder="Celular"
                        value={cellphoneTransfer}
                        onChange={(e) => setCellphoneTransfer(e.target.value)}
                      />
                    </FormControl>
                    <Button variant="outline" colorScheme="purple" onClick={handleSearchTransfer}>
                      Buscar
                    </Button>
                  </Flex>
                </Collapse>
              </SectionCard>
            )}

            <SectionCard mb={4} padding={4}>
              <Grid templateColumns={{ base: '1fr', md: '1fr 220px auto' }} gap={3} alignItems="end">
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700">
                    Buscar contacto
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Nombre, celular, correo o ID"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700">
                    Vista
                  </FormLabel>
                  <Select value={view} onChange={(e) => handleViewChange(e.target.value)}>
                    {DESK_VIEWS.map((el) => (
                      <option key={el.value} value={el.value}>
                        {el.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="ghost"
                  colorScheme="purple"
                  rightIcon={showFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  onClick={() => setShowFilter(!showFilter)}
                >
                  Más filtros
                </Button>
              </Grid>

              {/* Sin Collapse: recorta su contenido y escondía el calendario de las fechas. */}
              <Box display={showFilter ? 'block' : 'none'}>
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' }}
                  gap={3}
                  mt={4}
                  pt={4}
                  borderTop="1px solid"
                  borderColor="gray.100"
                >
                  <FormControl>
                    <FormLabel fontSize="sm">Estado</FormLabel>
                    <Select value={filterState} onChange={(e) => setFilterState(e.target.value)}>
                      <option value="">Según la vista</option>
                      {PROSPECT_STATUS.map((el) => (
                        <option key={el.value} value={el.value}>
                          {el.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Fecha inicio</FormLabel>
                    <DatePicker date={dateFilter} placeholder="Fecha inicio" onChange={setDateFilter} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Fecha fin</FormLabel>
                    <DatePicker date={dateFilterEnd} placeholder="Fecha fin" onChange={setDateFilterEnd} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Celular exacto</FormLabel>
                    <Input
                      type="number"
                      placeholder="Celular"
                      value={cellphone}
                      onChange={(e) => setCellphone(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Correo exacto</FormLabel>
                    <Input placeholder="Correo" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </FormControl>
                </Grid>
                <Flex gap={3} mt={4} justify="flex-end">
                  <Button variant="ghost" onClick={handleClearFilters}>
                    Limpiar
                  </Button>
                  <Button bg={BRAND} color="white" _hover={{ bg: '#463fd1' }} onClick={handleFilter}>
                    Aplicar filtros
                  </Button>
                </Flex>
              </Box>
            </SectionCard>

            <SectionCard padding={0} overflow="hidden">
              {fetching ? (
                <Flex justify="center" py={12}>
                  <Spinner color={BRAND} />
                </Flex>
              ) : visibleProspects.length === 0 ? (
                <EmptyState
                  title="No hay prospectos en esta vista"
                  description="Cambia la vista o los filtros. Si te escribió alguien por WhatsApp, regístralo con «Agregar prospecto»."
                />
              ) : (
                <TableContainer>
                  <Table size="md">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Prospecto</Th>
                        <Th>Contacto</Th>
                        <Th>Origen</Th>
                        <Th>Programa</Th>
                        <Th>Estado</Th>
                        <Th>Último contacto</Th>
                        {transferMode && <Th>Asesora actual</Th>}
                        <Th textAlign="right">Detalle</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {visibleProspects.map((prospect) => {
                        const status = statusInfo(prospect.status)
                        const label = programLabel(prospect.program, programTypes)
                        return (
                          <Tr key={prospect.id} _hover={{ bg: 'gray.50' }}>
                            <Td>
                              <Text fontWeight="600" color="gray.800" whiteSpace="normal" minW="160px">
                                {prospect.name}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                #{prospect.id} · {dayjs(prospect.createdAt).format('DD/MM/YYYY h:mm A')}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm">
                                {prospect?.country?.phone_code ? `+${prospect.country.phone_code} ` : ''}
                                {prospect.cellphone}
                              </Text>
                              <Text fontSize="xs" color="gray.500" whiteSpace="normal" wordBreak="break-all">
                                {prospect.email || 'Sin correo'}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{channelLabel(prospect)}</Text>
                              {prospect.from && (
                                <Text fontSize="xs" color="gray.500">
                                  Contactar por: {prospect.from}
                                </Text>
                              )}
                            </Td>
                            <Td>
                              <Text fontSize="sm" whiteSpace="normal" minW="160px">
                                {label}
                              </Text>
                              {prospect.program && !programTypes.some((el) => el.slug === prospect.program) && label !== 'Por definir' && (
                                <Text fontSize="xs" color="gray.400" whiteSpace="normal" wordBreak="break-all">
                                  {prospect.program}
                                </Text>
                              )}
                            </Td>
                            <Td>
                              <StatusBadge label={status.label} color={status.color} />
                            </Td>
                            <Td>
                              {(() => {
                                const last = lastContacts[prospect.id]
                                const days = last ? daysSince(last) : null
                                return (
                                  <>
                                    <StatusBadge
                                      label={last ? relativeDays(days) : 'Sin contacto'}
                                      color={contactColor(days)}
                                    />
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                      {last
                                        ? dayjs(last).format('DD/MM/YYYY')
                                        : `Ingresó ${relativeDays(daysSince(prospect.createdAt))}`}
                                    </Text>
                                  </>
                                )
                              })()}
                            </Td>
                            {transferMode && (
                              <Td>
                                <Text fontSize="sm" whiteSpace="normal">
                                  {prospect.adviser?.user
                                    ? `${prospect.adviser.user.first_name} ${prospect.adviser.user.last_name}`
                                    : '—'}
                                </Text>
                              </Td>
                            )}
                            <Td textAlign="right">
                              <Flex gap={2} justify="flex-end">
                                {canTransfer && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="purple"
                                    onClick={() =>
                                      // En el Escritorio propio el prospecto no trae la asesora poblada: es la de la sesión.
                                      setTransferProspect(transferMode ? prospect : { ...prospect, adviser: { ...user.adviser, user } })
                                    }
                                  >
                                    Transferir
                                  </Button>
                                )}
                                <Button
                                  as={Link}
                                  to={`/plataforma/asesoras/ver-prospecto/${prospect.id}`}
                                  size="sm"
                                  variant="outline"
                                  colorScheme="purple"
                                >
                                  Ver
                                </Button>
                              </Flex>
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              {!fetching && (
                <AiPagination
                  pagination={{ page, pageSize: PAGE_SIZE, total, pageCount }}
                  noun="prospectos"
                  onPageChange={(nextPage) => loadProspects(view, appliedFilters, nextPage)}
                />
              )}
            </SectionCard>
          </TabPanel>

          <TabPanel px={0} pt={5}>
            <SectionCard padding={0} overflow="hidden">
              {prospectReminders.length === 0 ? (
                <EmptyState
                  title="No tienes recordatorios para hoy"
                  description="Los recordatorios se crean desde el detalle de cada prospecto."
                />
              ) : (
                <TableContainer>
                  <Table>
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Prospecto</Th>
                        <Th>Título</Th>
                        <Th>Mensaje</Th>
                        <Th>Hora</Th>
                        <Th>Estado</Th>
                        <Th textAlign="right">Detalle</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {prospectReminders.map((reminder) => {
                        const status = statusInfo(reminder.prospect?.status)
                        return (
                          <Tr key={reminder.id}>
                            <Td fontWeight="600">{reminder.prospect?.name || '—'}</Td>
                            <Td whiteSpace="normal">{reminder.title}</Td>
                            <Td whiteSpace="normal">{reminder.message}</Td>
                            <Td>{reminder.time ? reminder.time.slice(0, 5) : '—'}</Td>
                            <Td>
                              <StatusBadge label={status.label} color={status.color} />
                            </Td>
                            <Td textAlign="right">
                              {reminder.prospect?.id && (
                                <Button
                                  as={Link}
                                  to={`/plataforma/asesoras/ver-prospecto/${reminder.prospect.id}`}
                                  size="sm"
                                  variant="outline"
                                  colorScheme="purple"
                                >
                                  Ver
                                </Button>
                              )}
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <TransferProspectModal
        prospect={transferProspect}
        isOpen={Boolean(transferProspect)}
        onClose={() => setTransferProspect(null)}
        onTransferred={handleTransferred}
      />
    </Box>
  )
}

export default HomeAsesoras
